import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Modal from "../Modal";
import { CloseIcon } from "../../icons/CloseIcon/CloseIcon";
import { getAuthHeaders } from "../../utils/authHeaders";
import { useUser } from "../../Data/UserContext";
import { formatMatchNameAbbreviated } from "../../utils/matchNameDisplay";
import { trackGameChallengeFlowOpened, trackGameChallengeSent } from "../../utils/frontendTrackingEvents";
import { whenModalHistoryIdle } from "../../utils/modalHistoryBack";
import { friendFacingName } from "../../utils/friendDisplayName";
import type { SquaresChallengeFriend } from "./SquaresChallengeFriendModal";

export type BullionMyCreatedPrivateContest = {
  gameId: number;
  matchId: string;
  name: string;
  entryStake: number;
  prizePot: number;
  playersCount: number;
  inviteCode: string | null;
  matchName: string | null;
  competition: string | null;
  startTime: string | null;
  createdAt: string | null;
};

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  friend: SquaresChallengeFriend | null;
  /** GTM-style locations e.g. my_zone_friends, public_profile */
  analyticsEntryPoint?: string;
  /**
   * Leave this modal and open the challenged battle.
   * Profile overlay supplies this so the profile closes before navigation.
   * When omitted, this modal closes itself and navigates after its history entry is released.
   */
  onOpenMatch?: (href: string) => void;
};

function formatFixtureStart(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

type PriorChallengeRow = {
  id: number;
  match_id: string | null;
  match_name: string | null;
  game_id: number | null;
  game_name: string | null;
  invite_code?: string | null;
  match_open?: boolean;
  game_open?: boolean;
};

type ChallengeSuccessEntry = {
  key: string;
  gameId: number;
  contestLabel: string;
  matchLabel: string;
  friendLabel: string;
  matchOpen?: boolean;
  matchId?: string | null;
  inviteCode?: string | null;
};

function bullionChallengeHref(matchId: string | null | undefined, gameId: number, inviteCode: string | null | undefined): string {
  const mid = matchId != null ? String(matchId).trim() : "";
  if (!mid) return "/bullion";
  const inv = inviteCode != null && String(inviteCode).trim() !== "" ? String(inviteCode).trim() : "";
  return `/bullion?match=${encodeURIComponent(mid)}&game=${encodeURIComponent(String(gameId))}${inv ? `&invite=${encodeURIComponent(inv)}` : ""}`;
}

/** One row per game_id */
function dedupeBullionChallengeEntriesByGameId(entries: ChallengeSuccessEntry[]): ChallengeSuccessEntry[] {
  const seen = new Set<number>();
  const out: ChallengeSuccessEntry[] = [];
  for (const e of entries) {
    const gid = Number(e.gameId);
    if (!Number.isFinite(gid) || gid <= 0) continue;
    if (seen.has(gid)) continue;
    seen.add(gid);
    out.push(e);
  }
  return out;
}

/** Above ProfileModalShell (z-10050) so challenge opens in front of the profile overlay. */
const CHALLENGE_MODAL_OVERLAY_Z = 10060;
const CHALLENGE_MODAL_CONTENT_Z = 10061;

export default function BullionPrivateContestsFriendModal({
  isOpen,
  onRequestClose,
  friend,
  analyticsEntryPoint,
  onOpenMatch,
}: Props) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const challengeFlowOpenedTrackedRef = useRef(false);
  const [games, setGames] = useState<BullionMyCreatedPrivateContest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [successEntries, setSuccessEntries] = useState<ChallengeSuccessEntry[]>([]);
  const [priorChallengesLoading, setPriorChallengesLoading] = useState(false);

  const friendLabel = useMemo(
    () => friendFacingName(friend) || "your friend",
    [friend]
  );

  const loadGames = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetch(`${process.env.API_URL}/bullion/private-games/my-created`, {
          method: "GET",
          headers: getAuthHeaders(),
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.message || "Unable to load private battles.");
        }
        const raw = Array.isArray(payload?.games) ? payload.games : [];
        const normalized: BullionMyCreatedPrivateContest[] = raw.map((g: Record<string, unknown>) => ({
          gameId: Number(g.gameId),
          matchId: String(g.matchId ?? ""),
          name: String(g.name ?? ""),
          entryStake: Number(g.entryStake ?? 0),
          prizePot: Number(g.prizePot ?? 0),
          playersCount: Number(g.playersCount ?? 0),
          inviteCode: g.inviteCode != null && String(g.inviteCode).trim() !== "" ? String(g.inviteCode).trim() : null,
          matchName: g.matchName != null ? String(g.matchName) : null,
          competition: g.competition != null ? String(g.competition) : null,
          startTime: g.startTime != null ? String(g.startTime) : null,
          createdAt: g.createdAt != null ? String(g.createdAt) : null,
        }));
        setGames(normalized.filter((x) => Number.isFinite(x.gameId) && x.gameId > 0 && x.matchId.trim() !== ""));
      } catch (e) {
        setGames([]);
        setError(e instanceof Error ? e.message : "Unable to load private battles.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !friend) return;
    loadGames();
  }, [isOpen, friend, loadGames]);

  useEffect(() => {
    if (!isOpen) {
      challengeFlowOpenedTrackedRef.current = false;
      setGames([]);
      setError(null);
      setSuccessEntries([]);
      setPriorChallengesLoading(false);
      setSendingId(null);
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !friend || user?.userId == null) return;
    if (!analyticsEntryPoint?.trim()) return;
    if (challengeFlowOpenedTrackedRef.current) return;
    challengeFlowOpenedTrackedRef.current = true;
    trackGameChallengeFlowOpened({
      user_id: user.userId,
      game_type: "bullion",
      entry_point: analyticsEntryPoint.trim(),
      pathname: location.pathname,
    });
  }, [isOpen, friend, user?.userId, analyticsEntryPoint, location.pathname]);

  useEffect(() => {
    if (!isOpen || !friend) return;
    const fid = friend.userId;
    const fl = friendFacingName(friend) || "your friend";
    let cancelled = false;
    setPriorChallengesLoading(true);
    void (async () => {
      try {
        const res = await fetch(
          `${process.env.API_URL}/account/friends/bullion-challenges-sent?friend_id=${encodeURIComponent(String(fid))}`,
          { headers: getAuthHeaders() }
        );
        const data = (await res.json().catch(() => ({}))) as { challenges?: PriorChallengeRow[] };
        if (cancelled) return;
        if (!res.ok) {
          setSuccessEntries([]);
          return;
        }
        const list = Array.isArray(data.challenges) ? data.challenges : [];
        const entries = dedupeBullionChallengeEntriesByGameId(
          list
            .filter((c) => c.game_id != null && Number.isFinite(c.game_id) && c.game_id > 0)
            .map((c) => {
              const midLabel =
                formatMatchNameAbbreviated(c.match_name || "") ||
                (c.match_name != null && String(c.match_name).trim() !== ""
                  ? String(c.match_name).trim()
                  : "this match");
              const contest = (c.game_name != null && String(c.game_name).trim() !== ""
                ? String(c.game_name).trim()
                : "Private battle") as string;
              return {
                key: `prior-${c.id}`,
                gameId: Number(c.game_id),
                contestLabel: contest,
                matchLabel: midLabel,
                friendLabel: fl,
                matchOpen: c.game_open !== false,
                matchId: c.match_id != null && String(c.match_id).trim() !== "" ? String(c.match_id) : null,
                inviteCode:
                  c.invite_code != null && String(c.invite_code).trim() !== "" ? String(c.invite_code).trim() : null,
              };
            })
        ).slice(0, 8);
        setSuccessEntries(entries);
      } catch {
        if (!cancelled) setSuccessEntries([]);
      } finally {
        if (!cancelled) setPriorChallengesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, friend?.userId, friend?.player_name, friend?.username]);

  useEffect(() => {
    if (!isOpen || !friend) return;
    const onDirty = () => {
      const fid = friend.userId;
      const fl = friendFacingName(friend) || "your friend";
      setPriorChallengesLoading(true);
      void (async () => {
        try {
          const res = await fetch(
            `${process.env.API_URL}/account/friends/bullion-challenges-sent?friend_id=${encodeURIComponent(String(fid))}`,
            { headers: getAuthHeaders() }
          );
          const data = (await res.json().catch(() => ({}))) as { challenges?: PriorChallengeRow[] };
          if (!res.ok) return;
          const list = Array.isArray(data.challenges) ? data.challenges : [];
          const entries = dedupeBullionChallengeEntriesByGameId(
            list
              .filter((c) => c.game_id != null && Number.isFinite(c.game_id) && c.game_id > 0)
              .map((c) => {
                const midLabel =
                  formatMatchNameAbbreviated(c.match_name || "") ||
                  (c.match_name != null && String(c.match_name).trim() !== ""
                    ? String(c.match_name).trim()
                    : "this match");
                const contest = (c.game_name != null && String(c.game_name).trim() !== ""
                  ? String(c.game_name).trim()
                  : "Private battle") as string;
                return {
                  key: `prior-${c.id}`,
                  gameId: Number(c.game_id),
                  contestLabel: contest,
                  matchLabel: midLabel,
                  friendLabel: fl,
                  matchOpen: c.game_open !== false,
                  matchId: c.match_id != null && String(c.match_id).trim() !== "" ? String(c.match_id) : null,
                  inviteCode:
                    c.invite_code != null && String(c.invite_code).trim() !== ""
                      ? String(c.invite_code).trim()
                      : null,
                };
              })
          ).slice(0, 8);
          setSuccessEntries(entries);
        } finally {
          setPriorChallengesLoading(false);
        }
      })();
    };
    window.addEventListener("c8-bullion-challenges-dirty", onDirty);
    return () => window.removeEventListener("c8-bullion-challenges-dirty", onDirty);
  }, [friend, isOpen]);

  const visibleSuccessEntries = useMemo(
    () => dedupeBullionChallengeEntriesByGameId(successEntries.filter((e) => e.matchOpen !== false)),
    [successEntries]
  );

  const openChallengedMatch = useCallback(
    (href: string) => {
      if (onOpenMatch) {
        onOpenMatch(href);
        return;
      }
      onRequestClose();
      whenModalHistoryIdle(() => navigate(href));
    },
    [navigate, onOpenMatch, onRequestClose]
  );

  const sendChallenge = useCallback(
    async (g: BullionMyCreatedPrivateContest) => {
      if (!friend) return;
      setSendingId(g.gameId);
      setError(null);
      try {
        const res = await fetch(`${process.env.API_URL}/account/friends/bullion-challenge`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ friend_id: friend.userId, game_id: g.gameId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const code = data?.error;
          if (code === "not_friends") setError("You can only challenge friends.");
          else if (code === "not_contest_host") setError("You can only challenge from battles you host.");
          else if (code === "contest_not_open" || code === "game_not_found")
            setError("That battle is no longer open.");
          else if (code === "missing_invite_code") setError("That battle cannot be shared yet. Try again.");
          else setError("Could not send challenge. Try again.");
          return;
        }
        if (user?.userId != null && analyticsEntryPoint?.trim()) {
          trackGameChallengeSent({
            user_id: user.userId,
            game_type: "bullion",
            entry_point: analyticsEntryPoint.trim(),
            challenged_friend_user_id: friend.userId,
            challenge_target_id: String(g.gameId),
            pathname: location.pathname,
          });
        }
        window.dispatchEvent(new CustomEvent("c8-bullion-challenges-dirty"));
        openChallengedMatch(bullionChallengeHref(g.matchId, g.gameId, g.inviteCode));
      } catch {
        setError("Could not send challenge. Try again.");
      } finally {
        setSendingId(null);
      }
    },
    [friend, user?.userId, analyticsEntryPoint, location.pathname, openChallengedMatch]
  );

  return (
    <Modal
      isOpen={isOpen && friend != null}
      onRequestClose={onRequestClose}
      contentLabel="Challenge friend to BULLION"
      ariaHideApp={false}
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          padding: 0,
          background: "transparent",
          width: "min(560px, calc(100vw - 16px))",
          maxWidth: "100%",
          maxHeight: "min(90vh, 720px)",
          overflow: "hidden",
          zIndex: CHALLENGE_MODAL_CONTENT_Z,
        },
        overlay: {
          background: "rgba(11, 2, 18, 0.8)",
          zIndex: CHALLENGE_MODAL_OVERLAY_Z,
        },
      }}
    >
      <div className="flex max-h-[min(90vh,720px)] flex-col overflow-hidden border border-[#2d1548] bg-[#2a0a55] shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 bg-[#e470ff] py-2 pl-3 pr-1">
          <span className="min-w-0" aria-hidden />
          <h2 className="text-center font-anton text-[17px] font-bold uppercase leading-none tracking-wide text-white max-[420px]:text-[15px]">
            BULLION challenge
          </h2>
          <div className="flex min-w-0 justify-end">
            <button
              type="button"
              onClick={onRequestClose}
              className="!flex !h-11 !w-11 shrink-0 !items-center !justify-center text-white transition-opacity hover:opacity-80"
              aria-label="Close"
            >
              <CloseIcon className="text-white" size={28} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#461d79] px-3 py-3 sm:px-4 sm:py-4">
          {priorChallengesLoading ? (
            <div className="rounded border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center font-chakra text-[14px] leading-snug text-white/70 max-[420px]:text-[13px]">
              Loading…
            </div>
          ) : visibleSuccessEntries.length === 0 ? (
            <div className="rounded border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center font-chakra text-[14px] leading-snug text-white max-[420px]:text-[13px]">
              <p>
                Challenge <span className="font-semibold">{friendLabel}</span> to play
                <span className="font-semibold text-[#d4ff1f]"> BULLION</span> with you.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" aria-live="polite">
              {visibleSuccessEntries.map((entry) => {
                const gFromList = games.find((x) => x.gameId === entry.gameId);
                const openTo = bullionChallengeHref(
                  entry.matchId ?? gFromList?.matchId,
                  entry.gameId,
                  entry.inviteCode ?? gFromList?.inviteCode
                );
                return (
                  <li key={entry.gameId} className="space-y-2">
                    <div className="border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 text-center">
                      <p className="font-chakra text-[13px] leading-snug text-white sm:text-[14px]">
                        You challenged <span className="font-semibold">{entry.friendLabel}</span> to{" "}
                        <span className="font-semibold text-[#d4ff1f]">{entry.contestLabel}</span> on{" "}
                        <span className="font-semibold text-[#d4ff1f]">{entry.matchLabel}</span>.
                      </p>
                      <Link
                        to={openTo}
                        onClick={(e) => {
                          e.preventDefault();
                          openChallengedMatch(openTo);
                        }}
                        className="inline-block font-chakra text-[13px] font-semibold text-[#d4ff1f] underline decoration-[#d4ff1f]/55 underline-offset-2 hover:text-white hover:decoration-white sm:text-[14px]"
                      >
                        Open battle in BULLION
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {error ? (
            <p
              className="font-chakra text-[13px] font-semibold sm:text-[14px]"
              style={{ color: "#ef4444" }}
              role="alert"
              aria-live="polite"
            >
              {error}
            </p>
          ) : null}
          {loading ? (
            <p className="text-center font-chakra text-sm text-white/60">Loading battles…</p>
          ) : games.length === 0 ? (
            <>
              <p className="font-chakra text-[14px] leading-snug text-white/70">
                No <strong className="text-white">open</strong> private battles yet. When you create one in BULLION, it
                will show up here so you can send a challenge.
              </p>
              <Link
                to="/bullion"
                onClick={(e) => {
                  e.preventDefault();
                  openChallengedMatch("/bullion");
                }}
                className="inline-block font-chakra text-[13px] font-semibold text-[#d4ff1f] underline decoration-[#d4ff1f]/55 underline-offset-2 hover:text-white hover:decoration-white sm:text-[14px]"
              >
                Open BULLION lobby
              </Link>
            </>
          ) : (
            <div
              className="flex flex-col gap-3 pb-1"
              style={{
                background: "linear-gradient(88.89deg, #652DAF 0%, #623998 100%)",
              }}
            >
              <ul className="flex flex-col gap-3 px-0 pt-1 sm:px-0" aria-label="Your private battles">
                {games.map((g) => {
                  const matchLine = formatMatchNameAbbreviated(g.matchName || "") || g.matchName || "Match";
                  const when = formatFixtureStart(g.startTime);
                  const metaBits = [g.competition, when].filter(Boolean);
                  const challengeDisabled = !g.inviteCode;
                  const challengeSending = sendingId === g.gameId;
                  const challengeOtherSending = sendingId != null && sendingId !== g.gameId;
                  const challengeCtaDisabled = challengeDisabled || challengeSending || challengeOtherSending;
                  const challengeCtaLabel = challengeSending ? "SENDING…" : "CHALLENGE";
                  return (
                    <li key={g.gameId} className="px-2">
                      <div className="border border-[#d4ff1f]/35 bg-[#1b062f]/55 px-3 py-2.5 sm:px-3.5">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-chakra text-[13px] leading-snug text-white sm:text-[14px]">
                              <span className="font-semibold text-[#d4ff1f]">
                                {g.name.trim() || "Private battle"}
                              </span>
                            </p>
                            <p className="mt-1 font-chakra text-[13px] leading-snug text-white sm:text-[14px]">
                              <span className="font-semibold text-white">{matchLine}</span>
                              {metaBits.length > 0 ? (
                                <span className="text-white/75">
                                  {" "}
                                  · {metaBits.join(" · ")}
                                </span>
                              ) : null}
                            </p>
                            <p className="mt-1 font-chakra text-[12px] text-white/65 sm:text-[13px]">
                              Entry {g.entryStake.toLocaleString()} gold balls · {g.playersCount} player
                              {g.playersCount === 1 ? "" : "s"}
                              {g.prizePot > 0 ? (
                                <>
                                  {" "}
                                  · Pot {g.prizePot.toLocaleString()}
                                </>
                              ) : null}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={challengeDisabled || challengeCtaDisabled}
                            onClick={() => sendChallenge(g)}
                            className={`shrink-0 bg-lime font-anton text-[12px] leading-[18px] px-3 py-0 uppercase whitespace-nowrap min-w-[80px] flex items-center justify-center text-main ${
                              challengeDisabled || challengeCtaDisabled
                                ? "cursor-not-allowed opacity-60"
                                : "hover:bg-limehover cursor-pointer"
                            }`}
                            style={{ letterSpacing: "0%", textAlign: "center" }}
                          >
                            {challengeCtaLabel}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-white/10 px-2 pb-2 pt-1 text-center">
                <Link
                  to="/bullion"
                  onClick={(e) => {
                    e.preventDefault();
                    openChallengedMatch("/bullion");
                  }}
                  className="inline-block font-chakra text-[13px] font-semibold text-[#d4ff1f] underline decoration-[#d4ff1f]/55 underline-offset-2 hover:text-white hover:decoration-white sm:text-[14px]"
                >
                  Create more private battles
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
